// Para qué cuenta se está operando, y si hay que preguntarlo antes de dejar
// operar. Decisiones puras de las pantallas que entran a una cuenta por enlace
// (`id_token_hint`): /subscribe y /change-password.
//
// Tres situaciones, según lo que responde el backend al canjear el enlace:
//
//   - `account_mismatch`: este navegador había iniciado sesión como OTRA
//     cuenta. No hay sesión ni dato alguno de la cuenta del enlace → bloqueo.
//   - cuenta corroborada (`account_confirmed: true`): login con contraseña en
//     esta pantalla, o el navegador ya había iniciado sesión como esa cuenta.
//   - cuenta solo afirmada por el enlace (`account_confirmed: false`): se puede
//     MIRAR el plan, pero antes de cualquier cambio se pregunta "¿es tu cuenta?".
//
// El default ante un dato ausente es el restrictivo: sin `account_confirmed`
// explícito en una entrada por enlace, la cuenta NO se da por corroborada.

/** Cómo llegó la identidad a la pantalla. */
export type EntryMode = 'hint' | 'login';

/** Lo que la pantalla sabe de la cuenta sobre la que opera. */
export interface AccountView {
  /** Email enmascarado (`ju***@gmail.com`), o null si el backend no lo informó. */
  hint: string | null;
  /** true = identidad corroborada; false = solo la afirma el enlace. */
  confirmed: boolean;
}

/**
 * El enlace (`id_token_hint`) es de una cuenta distinta de la que inició sesión
 * en este navegador. No trae sesión ni dato alguno de la cuenta del enlace.
 */
export interface AccountMismatch {
  account_mismatch: true;
  /** Ausente = el enlace es de otra cuenta. */
  reason?: string;
  return_url_ok?: boolean;
}

/**
 * Por qué el backend no dejó entrar con el enlace:
 *  - other_account: este navegador inició sesión como otra cuenta.
 *  - link_used_elsewhere: el enlace ya se abrió en otro navegador o dispositivo.
 *  - link_expired: pasó demasiado tiempo desde que se generó.
 */
export type MismatchReason = 'other_account' | 'link_used_elsewhere' | 'link_expired';

export function mismatchReasonOf(r: { reason?: unknown }): MismatchReason {
  return r.reason === 'link_used_elsewhere' || r.reason === 'link_expired'
    ? r.reason
    : 'other_account';
}

/** Type guard: el backend rechazó el enlace por ser de otra cuenta. */
export function isAccountMismatch(r: unknown): r is AccountMismatch {
  return (
    typeof r === 'object' &&
    r !== null &&
    (r as { account_mismatch?: unknown }).account_mismatch === true
  );
}

/**
 * Qué sabe la pantalla de la cuenta tras un init exitoso.
 *
 * Un login con contraseña en esta misma pantalla corrobora la identidad sin
 * depender de lo que diga la respuesta. En una entrada por enlace solo vale un
 * `account_confirmed === true` explícito.
 */
export function accountViewOf(
  res: { account_hint?: unknown; account_confirmed?: unknown },
  mode: EntryMode,
): AccountView {
  const hint =
    typeof res.account_hint === 'string' && res.account_hint.trim() ? res.account_hint.trim() : null;
  return { hint, confirmed: mode === 'login' || res.account_confirmed === true };
}

export type InitScreen<T> =
  | { kind: 'mismatch'; returnUrlOk: boolean; reason: MismatchReason }
  | { kind: 'session'; session: T; account: AccountView };

/**
 * Qué pantalla corresponde a la respuesta del init. `mismatch` se mira primero
 * y gana siempre: una respuesta que lo trae no se usa para nada más, aunque
 * viniera con otros campos.
 */
export function initScreen<T extends object>(
  res: T | AccountMismatch,
  mode: EntryMode,
): InitScreen<T> {
  if (isAccountMismatch(res)) {
    return { kind: 'mismatch', returnUrlOk: res.return_url_ok === true, reason: mismatchReasonOf(res) };
  }
  const fields = res as { account_hint?: unknown; account_confirmed?: unknown };
  return { kind: 'session', session: res, account: accountViewOf(fields, mode) };
}

/**
 * ¿Hay que preguntar "¿es tu cuenta?" antes de ejecutar una acción que cambia
 * algo? Sí cuando la identidad no está corroborada y el usuario todavía no la
 * confirmó en esta visita. Sin cuenta identificada no hay nada que confirmar
 * (ni sesión con la que operar).
 */
export function needsAccountConfirmation(
  account: AccountView | null,
  acknowledged: boolean,
): boolean {
  if (!account) return false;
  return !account.confirmed && !acknowledged;
}

/**
 * Qué hacer al entrar con un pago en curso guardado en este navegador
 * (lib/checkout-resume.ts):
 *   - discard:     el plan de ese pago ya no existe; no hay contra qué
 *                  confirmarlo desde acá (lo levanta la reconciliación).
 *   - ask-account: confirmar ese pago lo acredita en la cuenta del enlace, así
 *                  que es una acción como pagar o cambiar de plan: si la cuenta
 *                  solo la afirma el enlace, antes se pregunta "¿es tu cuenta?".
 *   - confirm:     cuenta corroborada (o ya confirmada en esta visita).
 *
 * `account` es obligatoria a propósito: la pantalla la pasa explícita en vez de
 * leerla de un estado que puede no estar actualizado todavía.
 */
export type ResumeDecision = 'discard' | 'ask-account' | 'confirm';

export function resumeDecision(input: {
  planFound: boolean;
  account: AccountView;
  acknowledged: boolean;
}): ResumeDecision {
  if (!input.planFound) return 'discard';
  return needsAccountConfirmation(input.account, input.acknowledged) ? 'ask-account' : 'confirm';
}

// Parámetros que identifican (o directamente autentican) a una cuenta y no
// deben quedar en la barra de direcciones: se comparten al copiar la URL y
// quedan en el historial.
export const IDENTITY_PARAMS: readonly string[] = ['id_token_hint'];

/**
 * Los mismos parámetros sin los de identidad, o null si no había ninguno (no
 * hay nada que reescribir). No muta el original; conserva el resto y su orden.
 */
export function withoutIdentityParams(
  params: URLSearchParams,
  names: readonly string[] = IDENTITY_PARAMS,
): URLSearchParams | null {
  if (!names.some((n) => params.has(n))) return null;
  const next = new URLSearchParams(params);
  for (const n of names) next.delete(n);
  return next;
}
